import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys, useAuthenticatedClient } from '@/api/queries'
import type { components } from '@/api/schema'
import { useBlobUrl } from '@/lib/use-blob-url'
import type { StatusTone } from '@/lib/status-tones'
import { parseUTCDate } from '@/lib/utils'

// Store customers' tax-exemption forms (USA-1504): the AI's reading, and staff approve, decline or reject.

type Schemas = components['schemas']

export type TaxExemption = Schemas['TaxExemptionRead']
export type TaxExemptionDetail = Schemas['TaxExemptionDetailRead']
export type TaxExemptionCheck = Schemas['TaxExemptionCheck']
export type TaxExemptionOrder = Schemas['TaxExemptionOrderRead']
export type TaxExemptionStatus = TaxExemption['status']
export type CertificateKind = NonNullable<TaxExemption['certificate_kind']>
export type TaxExemptionFilter = Exclude<TaxExemptionStatus, 'replaced'> | 'all'

export const TAX_EXEMPTION_FILTERS = [
  'needs_review',
  'reviewing',
  'approved',
  'declined',
  'revoked',
  'expired',
  'all',
] as const satisfies readonly TaxExemptionFilter[]

export const FILTER_LABELS: Record<TaxExemptionFilter, string> = {
  needs_review: 'Needs a person',
  reviewing: 'Checking',
  approved: 'Approved',
  declined: 'Declined',
  revoked: 'Rejected later',
  expired: 'Expired',
  all: 'All forms',
}

export const STATUS: Record<TaxExemptionStatus, { label: string; tone: StatusTone }> = {
  needs_review: { label: 'Needs a person', tone: 'warning' },
  reviewing: { label: 'Checking', tone: 'progress' },
  approved: { label: 'Approved', tone: 'success' },
  declined: { label: 'Declined', tone: 'danger' },
  revoked: { label: 'Rejected', tone: 'danger' },
  expired: { label: 'Expired', tone: 'neutral' },
  replaced: { label: 'Replaced', tone: 'neutral' },
}

export const KIND_LABELS: Record<CertificateKind, string> = {
  resale: 'Resale certificate',
  nonprofit: 'Nonprofit exemption',
  government: 'Government exemption',
  tribal: 'Tribal exemption',
  manufacturing: 'Manufacturing exemption',
  agricultural: 'Agricultural exemption',
  other_exemption: 'Other exemption',
  not_a_certificate: 'Not a certificate',
}

export type ExemptionReason = NonNullable<TaxExemption['exemption_reason']>
export type Coverage = NonNullable<TaxExemption['coverage']>
export type ExemptCategory = TaxExemption['exempt_categories'][number]

export const REASON_LABELS: Record<ExemptionReason, string> = {
  resale: 'Resale',
  charitable: 'Charity or nonprofit',
  religious: 'Religious organization',
  educational: 'School',
  government: 'Government',
  tribal: 'Tribal',
  manufacturing: 'Manufacturing or processing',
  agricultural: 'Agricultural',
  other: 'Other',
}

/** What a partial exemption can cover, in catalog order (the backend's categories.py). */
export const CATEGORY_LABELS: Record<ExemptCategory, string> = {
  disposables: 'Disposables',
  packaging: 'Packaging',
  cooking_equipment: 'Cooking equipment',
  food_prep_equipment: 'Food prep equipment',
  refrigeration: 'Refrigeration',
  ice_machines: 'Ice machines',
  warewashing: 'Warewashing',
  holding_warming: 'Holding and warming',
  beverage_equipment: 'Beverage equipment',
  dinnerware_glassware_flatware: 'Dinnerware, glassware and flatware',
  furniture: 'Furniture',
  food_ingredients: 'Food and ingredients',
  other_equipment: 'Other equipment',
}
export const CATEGORY_KEYS = Object.keys(CATEGORY_LABELS) as ExemptCategory[]

/** "All items", or the covered categories in a sentence. */
export function coverageText(coverage: Coverage | null | undefined, categories: readonly ExemptCategory[]): string | null {
  if (coverage === 'all') return 'All items'
  if (coverage !== 'some') return null
  const labels = CATEGORY_KEYS.filter((key) => categories.includes(key)).map((key) => CATEGORY_LABELS[key])
  return labels.length ? `Only ${labels.join(', ').toLowerCase()}` : 'Some items'
}

export const CHECK_LABELS: Record<TaxExemptionCheck['code'], string> = {
  name_match: 'Name matches the account',
  duplicate_number: 'Number not used by another customer',
  expired: 'Not expired',
  ai_unavailable: 'AI read the form',
}

/** "AI would approve · 92% sure", in the words staff use. */
export function aiCall(form: Pick<TaxExemption, 'ai_decision' | 'ai_confidence' | 'status'>): {
  text: string
  tone: 'approve' | 'decline' | 'unsure' | 'none'
} {
  const percent = form.ai_confidence != null ? Math.round(Number(form.ai_confidence) * 100) : null
  const sure = percent != null && Number.isFinite(percent) ? ` · ${percent}% sure` : ''
  if (form.ai_decision === 'approve') return { text: `AI would approve${sure}`, tone: 'approve' }
  if (form.ai_decision === 'decline') return { text: `AI would decline${sure}`, tone: 'decline' }
  if (form.ai_decision === 'unsure') return { text: `AI is unsure${sure}`, tone: 'unsure' }
  if (form.status === 'reviewing') return { text: 'AI is reading it', tone: 'none' }
  return { text: 'AI could not read it', tone: 'none' }
}

/** Staff approve or decline forms the AI was unsure of, declined ones, and ones still being read. */
export function canDecide(status: TaxExemptionStatus) {
  return status === 'needs_review' || status === 'declined' || status === 'reviewing'
}

const shortDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
const dayTime = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
})

/** A stored UTC time as the viewer's day ("Oct 30, 2026"). */
export function formatDay(value: string | null | undefined) {
  return value ? shortDay.format(parseUTCDate(value)) : '—'
}

/** A stored UTC time as the viewer's day and time. */
export function formatDayTime(value: string | null | undefined) {
  return value ? dayTime.format(parseUTCDate(value)) : '—'
}

/** A calendar date ("2027-06-30", as read off the form) as "Jun 30, 2027"; free text stays as written. */
export function formatCalendarDate(value: string | null | undefined) {
  if (!value) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
  if (!match) return value.trim() || null
  return shortDay.format(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
}

/** A calendar date for a date field, or "" when the form gave none or free text. */
export function dateFieldValue(value: string | null | undefined) {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value.trim()) ? value.trim() : ''
}

/** The tax still owed on orders placed tax-free: the ones with a rate. */
export function estimatedTaxTotal(orders: TaxExemptionOrder[]) {
  return orders.reduce((sum, order) => sum + (order.estimated_tax != null ? Number(order.estimated_tax) : 0), 0)
}

const MAX_STATES = 4

/** "TX, OK, LA +3": the states a form covers, short. */
export function statesText(states: string[] | null | undefined) {
  const list = states ?? []
  if (!list.length) return null
  const shown = list.slice(0, MAX_STATES).join(', ')
  return list.length > MAX_STATES ? `${shown} +${list.length - MAX_STATES}` : shown
}

export const taxExemptionKeys = {
  all: ['tax-exemptions'] as const,
  list: (status: TaxExemptionFilter, search: string, page: number) =>
    ['tax-exemptions', 'list', status, search, page] as const,
  counts: ['tax-exemptions', 'counts'] as const,
  detail: (uuid: string) => ['tax-exemptions', 'detail', uuid] as const,
  file: (uuid: string) => ['tax-exemptions', 'file', uuid] as const,
  page: (uuid: string, page: number) => ['tax-exemptions', 'page', uuid, page] as const,
}

/** Never refetched: a stored form and its page images don't change. */
const FILE_QUERY_KINDS: readonly unknown[] = ['file', 'page']

export function useTaxExemptions(status: TaxExemptionFilter, search: string, page: number) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: taxExemptionKeys.list(status, search, page),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/tax-exemptions', {
        params: {
          query: { status: status === 'all' ? undefined : status, search: search || undefined, page, limit: 50 },
        },
      })
      if (error) throw error
      return data
    },
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  })
}

export function useTaxExemption(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: taxExemptionKeys.detail(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/tax-exemptions/{form_uuid}', {
        params: { path: { form_uuid: uuid } },
      })
      if (error) throw error
      return data
    },
    // A form being read by the AI settles within a minute.
    refetchInterval: (query) => (query.state.data?.status === 'reviewing' ? 10_000 : false),
  })
}

/** A file from the API (it needs the bearer token) as an object URL while it is shown. */
/** The uploaded file itself, for an image shown in place and the New tab and Download buttons. */
export function useTaxExemptionFile(uuid: string) {
  return useBlobUrl(
    taxExemptionKeys.file(uuid),
    `/dashboard/tax-exemptions/${uuid}/file`,
    'The file is no longer available.',
    'The file did not load.',
  )
}

/** One page of a PDF form (from 1) as an image: phones can't show a PDF inside the page. */
export function useTaxExemptionPage(uuid: string, page: number) {
  return useBlobUrl(
    taxExemptionKeys.page(uuid, page),
    `/dashboard/tax-exemptions/${uuid}/pages/${page}.png`,
    'This page is not available.',
    'This page did not load.',
  )
}

export type ApproveInput = {
  note?: string
  expires_on?: string
  coverage?: Coverage
  exempt_categories?: ExemptCategory[]
}
export type DeclineInput = { customer_message: string; note?: string; bill_tax: boolean }

/**
 * Approve or decline (reject, for an approved form). The status changes only once the server confirms:
 * the decision changes the customer's exemption in Shopify and may bill tax, so the button spins until then.
 */
function useDecision<T>(uuid: string, send: (input: T) => Promise<TaxExemptionDetail>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: send,
    onSuccess: (form) => queryClient.setQueryData(taxExemptionKeys.detail(uuid), form),
    // Lists, counts, this form and its action items refresh; the file and its pages never change.
    onSettled: (form) => {
      void queryClient.invalidateQueries({
        queryKey: taxExemptionKeys.all,
        predicate: (query) => !FILE_QUERY_KINDS.includes(query.queryKey[1]),
      })
      if (form?.action_thread_id) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.customerComms.detail(form.action_thread_id) })
      }
    },
  })
}

export function useApproveTaxExemption(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useDecision<ApproveInput>(
    uuid,
    async ({ note, expires_on, coverage, exempt_categories }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/tax-exemptions/{form_uuid}/approve', {
        params: { path: { form_uuid: uuid } },
        body: {
          note: note || null,
          expires_on: expires_on || null,
          coverage: coverage ?? null,
          exempt_categories: coverage === 'some' ? exempt_categories ?? [] : null,
        },
      })
      if (error) throw error
      return data
    },
  )
}

export function useDeclineTaxExemption(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useDecision<DeclineInput>(
    uuid,
    async ({ customer_message, note, bill_tax }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/tax-exemptions/{form_uuid}/decline', {
        params: { path: { form_uuid: uuid } },
        body: { customer_message, note: note || null, bill_tax },
      })
      if (error) throw error
      return data
    },
  )
}

export type UpdateInput = {
  coverage: Coverage
  exempt_categories: ExemptCategory[]
  expires_on: string | null
  note?: string
}

/** Change what an approved form covers or when it expires; the server emails the customer the change. */
export function useUpdateTaxExemption(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useDecision<UpdateInput>(uuid, async ({ coverage, exempt_categories, expires_on, note }) => {
    const client = await getClient()
    const { data, error } = await client.PATCH('/dashboard/tax-exemptions/{form_uuid}', {
      params: { path: { form_uuid: uuid } },
      body: {
        coverage,
        exempt_categories: coverage === 'some' ? exempt_categories : [],
        expires_on,
        note: note || null,
      },
    })
    if (error) throw error
    return data
  })
}

